/**
 * The partner offer sheet — its words (partner codes build item 5 / C5, 2026-09-27).
 *
 * WORDS PENDING PAUL'S APPROVAL. Every string a person sees on the sheet that replaces the
 * paywall for someone holding a partner discount lives here, in one place, so the owner
 * can approve and edit them 1:1 (the PR body lists them). Plain and short; no store trial
 * is offered (there are none after store day); the brand is always "Coach Cedar", in full
 * (review of #4115, M1).
 *
 * App Review (design §9, guideline 3.1.2): every purchase screen shows the price, the
 * billing period, "renews automatically until you cancel", and Terms / Privacy. The plan
 * lines carry the first two; `renews`, `terms` and `privacy` the rest.
 *
 * Prices: on Android the discounted price is Google Play's own (RevenueCat reads the
 * offer's price for this person's country). On iPhone the app cannot know it: Apple turns a
 * percentage into a price point per country from its own grid (design §2), and StoreKit
 * does not expose an offer code's price before it is redeemed. So the iPhone line states the
 * regular price (exact) and "N% off", and Apple's sheet shows the exact discounted price before
 * the person confirms (`appleExactPrice`). "N% off" is kept TRUE by a rule, not a hedge word
 * (owner, 2026-09-27; review of #4115, L5): when the owner creates the App Store offer for a
 * level, they pick, in every country, the price point AT OR BELOW the exact N%-off price — so
 * the real discount is never less than advertised (docs/architecture/38 §12 and §14; the Store
 * access page's Discount levels section says so). The lengths: a monthly discount lasts its
 * TERM, the months the server sends with the plan (Codes PR-2, migration 1845 — "first 3
 * months", "first month"; each term is its own store offer); without one (a server before
 * PR-2) "first 12 months", as before. An annual discount is always "first year".
 *
 * Pure, no React. web/src/utils/__tests__/partnerOfferSheet.test.ts pins the lines.
 */

export const PARTNER_OFFER_SHEET = {
  /** The header. The partner's display name (partners.display_name). */
  title: (partnerName: string) => `Your offer from ${partnerName}`,
  /** The header, when the partner's name did not arrive. */
  titleNoName: 'Your partner offer',
  /** Under the header. */
  subtitle: 'Pick a plan to continue with Coach Cedar.',
  /** A plan's period, after the tier's own name ("CC Chat+ · Monthly"). */
  monthly: 'Monthly',
  annual: 'Annual',
  /** The badge on each plan. */
  percentOff: (pct: number) => `${pct}% off`,
  /** iPhone plan lines: the level, the regular price (see the header note — the price-point
   *  rule) and, for a monthly plan, the term in months (absent → 12). */
  appleMonthlyTerms: (pct: number, regular: string, months?: number | null) =>
    months === 1
      ? `${pct}% off ${regular}/month for your first month, then ${regular}/month.`
      : `${pct}% off ${regular}/month for your first ${months ?? 12} months, then ${regular}/month.`,
  appleAnnualTerms: (pct: number, regular: string) =>
    `${pct}% off ${regular}/year for your first year, then ${regular}/year.`,
  /** iPhone, under the plans. */
  appleExactPrice: 'Apple shows your exact price before you confirm.',
  /** Android plan lines: Google Play's own price for the offer. */
  playMonthlyTerms: (discounted: string, months: number, regular: string) =>
    months === 1
      ? `${discounted} for your first month, then ${regular}/month.`
      : `${discounted}/month for your first ${months} months, then ${regular}/month.`,
  playAnnualTerms: (discounted: string, years: number, regular: string) =>
    years === 1
      ? `${discounted} for your first year, then ${regular}/year.`
      : `${discounted}/year for your first ${years} years, then ${regular}/year.`,
  /** The renewal disclosure (App Review). */
  renews: 'Renews automatically until you cancel.',
  terms: 'Terms of Use',
  and: 'and',
  privacy: 'Privacy Policy',
  /** The button (also its screen-reader label). */
  cta: 'Continue with Coach Cedar',
  /** Opens the normal paywall (every plan, regular prices). */
  seeAllPlans: 'See all plans',
  /** Closes the sheet. */
  notNow: 'Not now',
  /** Back from the store with no purchase seen yet. On iPhone Apple's redemption can land a
   *  little after the person comes back, so this never says "no purchase was made"
   *  (review of #4115, LOW-B). */
  noPurchaseYet:
    "We couldn't confirm a purchase yet. If you completed it, it'll show up shortly — your discount is still here if you didn't.",
  /** iPhone: the person has used their plan switches for this billing period; their discount
   *  is held for one plan (the server answered code_held_for_period; review of #4115, M-A).
   *  `plan` is the plan's name as the sheet shows it ("CC Chat+ · Monthly"). */
  heldForPlan: (plan: string) => `Your discount is saved for ${plan}. Pick it to continue, or see all plans.`,
  /** The App Store codes for this discount ran out (the server answered out_of_stock). */
  outOfStock: "Your discount can't be applied right now. Please try again later.",
  /** Anything else that went wrong (the network, the store). */
  somethingWrong: 'Something went wrong. Please try again.',
  /** iPhone: this person has been handed the most App Store offer codes a period allows (the
   *  server answered offer_code_limit). The server sends these words with the refusal; this is
   *  the same text for an answer that arrives without them. The owner's words (2026-09-30,
   *  services/partner_discount_purchase.OFFER_CODE_LIMIT_MESSAGE — keep the two identical). */
  offerCodeLimit:
    "You've already been given the most offer codes this offer allows. Message us in Support (Account tab) if you think that's wrong.",
  /** Screen readers: the sheet itself. */
  a11yDialog: 'Dialog: your partner offer',
  /** Screen readers, Android: the guard that holds the screen while a purchase runs (review of
   *  #4115, round 3, NIT-D). The owner's words (approved 2026-09-29). */
  a11yPurchaseInProgress: 'Completing your purchase. Please wait.',
} as const;

export const PARTNER_OFFER_SHEET_LINKS = {
  terms: 'https://coachcedar.com/terms',
  privacy: 'https://coachcedar.com/privacy',
} as const;
